import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
} from '@material-ui/core'
import EditOutlined from '@material-ui/icons/EditOutlined'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { fetchMarkets, MarketModel } from '../../redux/actions/dashboard'
import MarketDialog from './MarketDialog'
import DashboardSection, { BUTTON_WIDTH } from './DashboardSection'

const useStyles = makeStyles((theme) => ({
    table: {
        '& td:first-child, & th:first-child': {
            paddingLeft: theme.spacing(2),
        },
    },
    actionsCell: {
        width: 1,
        whiteSpace: 'nowrap',
        textAlign: 'right',
    },
    name: {
        whiteSpace: 'nowrap',
    },
    url: {
        wordBreak: 'break-all',
    },
    centered: {
        padding: theme.spacing(4),
    },
    footer: {
        marginTop: 'auto',
        padding: theme.spacing(2, 2, 2.5),
    },
    button: {
        width: BUTTON_WIDTH,
    },
}))

interface Props {
    editable?: boolean
    onSuccess?: (msg: string) => void
    onError?: (msg: string) => void
}

export default function Markets({ editable = false, onSuccess, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const markets: MarketModel[] | null = useSelector((state: any) => state.markets.value)
    const [dialog, setDialog] = useState<{ market?: MarketModel }>()

    useEffect(() => {
        dispatch(fetchMarkets())
    }, [dispatch])

    const columns = editable ? 4 : 3

    return (
        <DashboardSection title={t('dashboard.marketTitle')} dense>
            <Table size="small" className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('common.name')}</TableCell>
                        <TableCell>{t('dashboard.marketUrl')}</TableCell>
                        <TableCell>{t('common.description')}</TableCell>
                        {editable ? <TableCell className={classes.actionsCell} /> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!markets ? (
                        <TableRow>
                            <TableCell colSpan={columns} align="center" className={classes.centered}>
                                <CircularProgress size={32} />
                            </TableCell>
                        </TableRow>
                    ) : (
                        markets.map((market) => (
                            <TableRow key={market.id}>
                                <TableCell className={classes.name}>{market.name ?? t('common.none')}</TableCell>
                                <TableCell className={classes.url}>
                                    {market.url ? (
                                        <a href={market.url} target="_blank" rel="noopener noreferrer">
                                            {market.url}
                                        </a>
                                    ) : (
                                        t('common.none')
                                    )}
                                </TableCell>
                                <TableCell>{market.description ?? ''}</TableCell>
                                {editable ? (
                                    <TableCell className={classes.actionsCell}>
                                        <Tooltip title={t('dashboard.editMarket') as string}>
                                            <IconButton size="small" onClick={() => setDialog({ market })}>
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
            {editable ? (
                <div className={classes.footer}>
                    <Button className={classes.button} variant="contained" color="primary" onClick={() => setDialog({})}>
                        {t('dashboard.addMarket')}
                    </Button>
                </div>
            ) : null}
            {editable ? (
                <MarketDialog
                    open={Boolean(dialog)}
                    market={dialog?.market}
                    onClose={() => setDialog(undefined)}
                    onSaved={() => {
                        setDialog(undefined)
                        onSuccess?.(t('dashboard.marketSaved'))
                        dispatch(fetchMarkets())
                    }}
                    onError={(msg) => onError?.(msg)}
                />
            ) : null}
        </DashboardSection>
    )
}
